<script setup>
import { ref, nextTick, onMounted } from "vue";
import axios from "axios";
import Swal from "sweetalert2";
import SignaturePad from "signature_pad";
import { FingerprintSpinner } from "epic-spinners";

definePage({
  meta: {
    action: 'read',
    subject: 'WEB - RESPONSAVEIS E ASSINATURA',
  },
});

const loading = ref(false);
const funcionarios = ref([]);
const modalAberto = ref(false);
const modoEdicao = ref(false);
const funcionarioSelecionado = ref(null);
/** No modo novo: CODIGO do funcionário escolhido no select (evita bug de select com objeto). */
const codigoFuncionarioNovo = ref(null);
const canvasRef = ref(null);
const signaturePad = ref(null);
const fileInputRef = ref(null);
const modoAssinatura = ref("desenho"); // 'desenho' | 'upload'
const responsavelLiberacao = ref(true);

const userData = useCookie("userData");

const fetchFuncionarios = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(
      `${import.meta.env.VITE_URL_SITE}/api/funcionario-assinatura/index`
    );
    funcionarios.value = data || [];
  } catch (error) {
    Swal.fire(
      "Erro",
      error.response?.data?.message || "Falha ao carregar funcionários",
      "error"
    );
  } finally {
    loading.value = false;
  }
};

const inicializarSignaturePad = () => {
  const canvas = canvasRef.value;
  if (canvas) {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    signaturePad.value = new SignaturePad(canvas);
  }
};

const abrirModalNovo = () => {
  funcionarioSelecionado.value = null;
  codigoFuncionarioNovo.value = null;
  modoEdicao.value = false;
  responsavelLiberacao.value = true;
  modoAssinatura.value = "desenho";
  modalAberto.value = true;
  nextTick(() => {
    inicializarSignaturePad();
  });
};

const abrirModalEditar = (func) => {
  funcionarioSelecionado.value = { ...func };
  modoEdicao.value = true;
  responsavelLiberacao.value = func.RESPONSAVEL_LIBERACAO ?? true;
  modoAssinatura.value = "desenho";
  modalAberto.value = true;
  nextTick(() => {
    inicializarSignaturePad();
  });
};

const fecharModal = () => {
  modalAberto.value = false;
  funcionarioSelecionado.value = null;
  codigoFuncionarioNovo.value = null;
  signaturePad.value = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

const onFileSelected = (event) => {
  const file = event.target.files?.[0];
  if (!file) return;
  const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
  if (!validTypes.includes(file.type)) {
    Swal.fire("Erro", "Formato inválido. Use JPG, PNG ou GIF.", "error");
    return;
  }
  if (file.size > 2 * 1024 * 1024) {
    Swal.fire("Erro", "Arquivo deve ter no máximo 2MB.", "error");
    return;
  }
};

const salvarAssinatura = async () => {
  const codFuncionario = modoEdicao.value
    ? funcionarioSelecionado.value?.CODIGO
    : codigoFuncionarioNovo.value;
  if (!codFuncionario) {
    Swal.fire("Erro", "Selecione um funcionário.", "error");
    return;
  }

  let assinaturaBase64 = null;
  let arquivo = null;

  if (modoAssinatura.value === "desenho") {
    const vazio = !signaturePad.value || signaturePad.value.isEmpty();
    if (vazio) {
      // Se for edição e já tiver assinatura, permitimos vazio (mantém a atual)
      // Caso contrário (novo ou edição sem assinatura prévia), é obrigatório
      if (!modoEdicao.value || !funcionarioSelecionado.value?.TEM_ASSINATURA) {
        Swal.fire(
          "Erro",
          "Desenhe a assinatura ou faça upload de uma imagem.",
          "error"
        );
        return;
      }
    } else {
      assinaturaBase64 = signaturePad.value.toDataURL("image/png");
    }
  } else {
    const input = fileInputRef.value;
    const semArquivo = !input?.files?.length;
    if (semArquivo) {
      // Se for edição e já tiver assinatura, permitimos vazio
      if (!modoEdicao.value || !funcionarioSelecionado.value?.TEM_ASSINATURA) {
        Swal.fire("Erro", "Selecione uma imagem para upload.", "error");
        return;
      }
    } else {
      arquivo = input.files[0];
    }
  }

  loading.value = true;
  try {
    const formData = new FormData();
    formData.append("COD_FUNCIONARIO", String(codFuncionario));
    formData.append(
      "RESPONSAVEL_LIBERACAO",
      responsavelLiberacao.value ? "1" : "0"
    );
    if (assinaturaBase64) {
      formData.append("assinatura", assinaturaBase64);
    }
    if (arquivo) {
      formData.append("arquivo", arquivo);
    }

    await axios.post(
      `${import.meta.env.VITE_URL_SITE}/api/funcionario-assinatura/store`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    Swal.fire("Sucesso", "Assinatura salva com sucesso!", "success");
    fecharModal();
    fetchFuncionarios();
  } catch (error) {
    Swal.fire(
      "Erro",
      error.response?.data?.message || "Falha ao salvar assinatura",
      "error"
    );
  } finally {
    loading.value = false;
  }
};

const removerAssinatura = async (func) => {
  const result = await Swal.fire({
    title: "Remover assinatura?",
    text: `Deseja remover a assinatura de ${func.NOME}?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sim, remover",
    cancelButtonText: "Cancelar",
  });
  if (!result.isConfirmed) return;

  loading.value = true;
  try {
    await axios.delete(
      `${import.meta.env.VITE_URL_SITE}/api/funcionario-assinatura/destroy/${
        func.CODIGO
      }`
    );
    Swal.fire("Sucesso", "Assinatura removida com sucesso.", "success");
    fetchFuncionarios();
  } catch (error) {
    Swal.fire(
      "Erro",
      error.response?.data?.message || "Falha ao remover assinatura",
      "error"
    );
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchFuncionarios();
});
</script>

<template>
  <div>
    <div class="page-header">
      <div class="page-titles">
        <div class="breadcrumb">
          <span class="page-title-text">Responsáveis pela Liberação</span>
          <span class="pipe"> | </span>
          <span class="breadcrumb-icon" aria-hidden="true">
            <VIcon size="18" icon="tabler-signature" />
          </span>
          <span class="sep"> › </span>
          <span class="crumb-current">Assinaturas de Funcionários</span>
        </div>
      </div>
    </div>

    <div class="content-wrapper">
      <div class="info-box">
        <VIcon size="24" icon="tabler-info-circle" />
        <div>
          <p>
            <strong>Como cadastrar um responsável pela entrega e sua assinatura:</strong>
          </p>
          <ol class="info-steps">
            <li>Clique em <strong>Novo Responsável</strong>.</li>
            <li>Selecione o <strong>funcionário</strong> na lista.</li>
            <li>Marque <strong>Responsável pela entrega / liberação</strong> (para que ele seja um dos responsáveis).</li>
            <li>Adicione a <strong>assinatura</strong> (desenhando no quadro ou enviando uma imagem).</li>
            <li>Clique em <strong>Salvar</strong>.</li>
          </ol>
          <p class="info-hint">
            A assinatura é obrigatória para atuar como responsável. Caso já possua uma cadastrada, você pode editar o status de responsável mantendo a assinatura atual.
          </p>
        </div>
      </div>

      <div v-if="loading && !funcionarios.length" class="spinner-area">
        <fingerprint-spinner
          :animation-duration="1200"
          :size="48"
          color="#2c3e50" />
      </div>

      <div v-else>
        <div class="table-header-actions">
          <div class="table-title">
            <VIcon size="22" icon="tabler-users" />
            <span>Funcionários com Assinaturas</span>
            <span class="badge">{{ funcionarios.length }}</span>
          </div>
          <button class="btn-add" @click="abrirModalNovo">
            <VIcon size="20" icon="tabler-plus" />
            Novo Responsável
          </button>
        </div>

        <div v-if="!funcionarios.length" class="empty-state">
          <VIcon size="64" icon="tabler-user-off" />
          <h3>Nenhum funcionário encontrado</h3>
          <p>
            Adicione assinaturas aos funcionários que atuam como responsáveis ou
            entregadores.
          </p>
          <button class="btn-add" @click="abrirModalNovo">
            <VIcon size="20" icon="tabler-plus" />
            Cadastrar Assinatura
          </button>
        </div>

        <div v-else class="table-container">
          <table class="table">
            <thead>
              <tr>
                <th>
                  <VIcon size="18" icon="tabler-user" />
                  Nome
                </th>
                <th>
                  <VIcon size="18" icon="tabler-building" />
                  Departamento
                </th>
                <th>
                  <VIcon size="18" icon="tabler-briefcase" />
                  Função
                </th>
                <th>
                  <VIcon size="18" icon="tabler-shield-check" />
                  Responsável
                </th>
                <th>
                  <VIcon size="18" icon="tabler-signature" />
                  Assinatura
                </th>
                <th>
                  <VIcon size="18" icon="tabler-settings" />
                  Ações
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="f in funcionarios" :key="f.CODIGO">
                <td class="name-cell">{{ f.NOME }}</td>
                <td>{{ f.DEPARTAMENTO || "-" }}</td>
                <td>{{ f.FUNCAO || "-" }}</td>
                <td class="status-cell">
                  <span
                    v-if="f.RESPONSAVEL_LIBERACAO"
                    class="status-badge status-success">
                    <VIcon size="16" icon="tabler-check" />
                    Sim
                  </span>
                  <span v-else class="status-badge status-neutral">Não</span>
                </td>
                <td class="signature-cell">
                  <img
                    v-if="f.TEM_ASSINATURA && f.ASSINATURA_URL"
                    :src="f.ASSINATURA_URL"
                    alt="Assinatura"
                    class="signature-thumb" />
                  <span v-else class="no-signature">—</span>
                </td>
                <td class="actions-cell">
                  <button
                    class="action-button action-button-edit"
                    @click="abrirModalEditar(f)"
                    title="Editar">
                    <VIcon size="18" icon="tabler-pencil" />
                  </button>
                  <button
                    v-if="f.TEM_ASSINATURA"
                    class="action-button action-button-delete"
                    @click="removerAssinatura(f)"
                    title="Remover assinatura">
                    <VIcon size="18" icon="tabler-trash" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal -->
      <div v-if="modalAberto" class="modal">
        <div class="modal-overlay" @click="fecharModal"></div>
        <div class="modal-content modal-assinatura">
          <div class="modal-header">
            <VIcon size="24" icon="tabler-signature" />
            <h3>
              {{ modoEdicao ? "Editar Assinatura" : "Cadastrar Assinatura" }}
            </h3>
          </div>

          <div class="modal-body">
            <div class="form-group">
              <label>
                <VIcon size="18" icon="tabler-user" />
                Funcionário
              </label>
              <select
                v-if="modoEdicao"
                :value="funcionarioSelecionado?.CODIGO"
                class="select-func"
                disabled>
                <option :value="funcionarioSelecionado?.CODIGO">
                  {{ funcionarioSelecionado?.NOME }}
                </option>
              </select>
              <select
                v-else
                v-model="codigoFuncionarioNovo"
                class="select-func"
                required>
                <option :value="null">Selecione um funcionário...</option>
                <option
                  v-for="f in funcionarios"
                  :key="f.CODIGO"
                  :value="f.CODIGO">
                  {{ f.NOME }} {{ f.DEPARTAMENTO ? `(${f.DEPARTAMENTO})` : "" }}
                </option>
              </select>
              <p class="form-hint">
                {{ modoEdicao ? funcionarioSelecionado?.NOME : "Obrigatório para cadastrar assinatura." }}
              </p>
            </div>

            <div class="form-group checkbox-group">
              <label class="checkbox-label">
                <input
                  v-model="responsavelLiberacao"
                  type="checkbox"
                  class="checkbox-input" />
                <span>Responsável pela entrega / liberação</span>
              </label>
              <p class="form-hint">
                Marque esta opção para que este funcionário seja um dos responsáveis pela entrega.
                A assinatura é obrigatória para habilitar esta função (caso já exista uma, não é necessário alterá-la).
              </p>
            </div>

            <div class="form-group">
              <label>Como adicionar a assinatura?</label>
              <div class="tabs-assinatura">
                <button
                  type="button"
                  :class="['tab-btn', modoAssinatura === 'desenho' && 'active']"
                  @click="modoAssinatura = 'desenho'">
                  <VIcon size="20" icon="tabler-pencil" />
                  Assinar na hora
                </button>
                <button
                  type="button"
                  :class="['tab-btn', modoAssinatura === 'upload' && 'active']"
                  @click="modoAssinatura = 'upload'">
                  <VIcon size="20" icon="tabler-upload" />
                  Upload de imagem
                </button>
              </div>
            </div>

            <div v-if="modoAssinatura === 'desenho'" class="canvas-area">
              <canvas
                ref="canvasRef"
                width="400"
                height="180"
                class="signature-canvas"></canvas>
              <button
                type="button"
                class="btn-clear"
                @click="signaturePad?.clear()">
                <VIcon size="18" icon="tabler-eraser" />
                Limpar
              </button>
            </div>

            <div v-else class="upload-area">
              <input
                ref="fileInputRef"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif"
                @change="onFileSelected"
                class="file-input" />
              <p class="upload-hint">JPG, PNG ou GIF. Máximo 2MB.</p>
            </div>
          </div>

          <div class="modal-buttons">
            <button class="btn-secondary" @click="fecharModal">
              <VIcon size="18" icon="tabler-x" />
              Cancelar
            </button>
            <button
              class="btn-primary"
              @click="salvarAssinatura"
              :disabled="loading">
              <VIcon size="18" icon="tabler-check" />
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-header {
  margin-bottom: 1.5rem;
}

.breadcrumb {
  font-size: 0.875rem;
  color: #7f8c8d;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-title-text {
  font-weight: 700;
  color: #2c3e50;
}

.content-wrapper {
  background: #fff;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.info-box {
  display: flex;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: #e3f2fd;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border-left: 4px solid #1e88e5;
}

.info-box p {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
  color: #1565c0;
}

.info-hint {
  font-size: 0.85rem !important;
  opacity: 0.9;
}

.info-steps {
  margin: 0.5rem 0 1rem 1.25rem;
  padding-left: 0.5rem;
  font-size: 0.95rem;
  color: #1565c0;
  line-height: 1.6;
}

.info-steps li {
  margin-bottom: 0.35rem;
}

.spinner-area {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.table-header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.table-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
}

.badge {
  background: #1e88e5;
  color: #fff;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
}

.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  background: linear-gradient(135deg, #1e88e5 0%, #1565c0 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(30, 136, 229, 0.3);
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #999;
}

.empty-state h3 {
  margin: 1rem 0 0.5rem;
  font-size: 1.5rem;
  color: #555;
}

.empty-state p {
  color: #777;
  margin-bottom: 1.5rem;
}

.table-container {
  overflow-x: auto;
  border: 1px solid #e0e4e7;
  border-radius: 8px;
}

.table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
}

.table th {
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 700;
  font-size: 0.875rem;
  color: #495057;
  border-bottom: 2px solid #e0e4e7;
}

.table td {
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
}

.table tbody tr:hover {
  background: #f8f9fa;
}

.name-cell {
  font-weight: 600;
  color: #2c3e50;
}

.status-cell {
  text-align: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
}

.status-success {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-neutral {
  background: #e0e4e7;
  color: #6c757d;
}

.signature-cell {
  text-align: center;
}

.signature-thumb {
  max-height: 40px;
  max-width: 120px;
  object-fit: contain;
}

.no-signature {
  color: #adb5bd;
  font-style: italic;
}

.actions-cell {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
}

.action-button {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-button-edit {
  background: #e3f2fd;
  color: #1565c0;
}

.action-button-edit:hover {
  background: #bbdefb;
}

.action-button-delete {
  background: #ffebee;
  color: #c62828;
}

.action-button-delete:hover {
  background: #ffcdd2;
}

/* Modal */
.modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

.modal-assinatura {
  position: relative;
  background: #fff;
  padding: 2rem;
  border-radius: 12px;
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e4e7;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
}

.modal-body {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  color: #495057;
  margin-bottom: 0.5rem;
}

.select-func {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1.5px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.95rem;
  background: #fff;
}

.form-hint {
  margin-top: 0.35rem;
  font-size: 0.8rem;
  color: #6c757d;
}

.checkbox-group .checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  margin-bottom: 0;
}

.checkbox-input {
  width: 18px;
  height: 18px;
  accent-color: #1e88e5;
  cursor: pointer;
}

.tabs-assinatura {
  display: flex;
  gap: 0.5rem;
}

.tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1.5px solid #dee2e6;
  border-radius: 8px;
  background: #fff;
  font-weight: 600;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  border-color: #1e88e5;
  color: #1e88e5;
}

.tab-btn.active {
  border-color: #1e88e5;
  background: #e3f2fd;
  color: #1565c0;
}

.canvas-area {
  border: 2px solid #e0e4e7;
  border-radius: 8px;
  padding: 1rem;
  background: #fafafa;
}

.signature-canvas {
  display: block;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  cursor: crosshair;
}

.btn-clear {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.5rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-clear:hover {
  background: #fff3e0;
  border-color: #ff9800;
  color: #e65100;
}

.upload-area {
  padding: 1.5rem;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  text-align: center;
  background: #f8f9fa;
}

.file-input {
  width: 100%;
  padding: 0.5rem;
  font-size: 0.9rem;
}

.upload-hint {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.modal-buttons {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e4e7;
}

.btn-primary,
.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary {
  background: linear-gradient(135deg, #1e88e5 0%, #1565c0 100%);
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(30, 136, 229, 0.3);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #e9ecef;
  color: #495057;
  border: 1.5px solid #dee2e6;
}

.btn-secondary:hover {
  background: #dee2e6;
}

@media (max-width: 768px) {
  .table-header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .table th,
  .table td {
    padding: 0.75rem;
    font-size: 0.875rem;
  }

  .modal-assinatura {
    padding: 1.5rem;
  }
}

/* ===== DARK MODE ===== */
.v-theme--dark .breadcrumb {
  color: #d8dee9;
}

.v-theme--dark .page-title-text {
  color: #eceff4;
}

.v-theme--dark .content-wrapper {
  background-color: #252836;
  color: #eceff4;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.v-theme--dark .info-box {
  background: #3b4252;
  border-left-color: #5e81ac;
}

.v-theme--dark .info-box p {
  color: #88c0d0;
}

.v-theme--dark .info-steps {
  color: #88c0d0;
}

.v-theme--dark .table-title {
  color: #eceff4;
}

.v-theme--dark .table-title .badge {
  background: #5e81ac;
}

.v-theme--dark .table-container {
  border-color: #4c566a;
}

.v-theme--dark .table {
  background: #252836;
  color: #eceff4;
}

.v-theme--dark .table th {
  background: #2e344c;
  color: #d8dee9;
  border-bottom-color: #4c566a;
}

.v-theme--dark .table td {
  border-bottom-color: #4c566a;
  color: #eceff4;
}

.v-theme--dark .table tbody tr:hover {
  background: #3b4252;
}

.v-theme--dark .name-cell {
  color: #eceff4;
}

.v-theme--dark .status-success {
  background: #2e7d32;
  color: #c8e6c9;
}

.v-theme--dark .status-neutral {
  background: #434c6e;
  color: #81a1c1;
}

.v-theme--dark .no-signature {
  color: #6c7a89;
}

.v-theme--dark .action-button-edit {
  background: #434c6e;
  color: #88c0d0;
}

.v-theme--dark .action-button-edit:hover {
  background: #4c566a;
  color: #88c0d0;
}

.v-theme--dark .action-button-delete {
  background: #4c3a3a;
  color: #bf616a;
}

.v-theme--dark .action-button-delete:hover {
  background: #5c4444;
}

.v-theme--dark .empty-state {
  color: #d8dee9;
}

.v-theme--dark .empty-state h3 {
  color: #eceff4;
}

.v-theme--dark .empty-state p {
  color: #81a1c1;
}

.v-theme--dark .modal-overlay {
  background: rgba(0, 0, 0, 0.7);
}

.v-theme--dark .modal-assinatura {
  background: #252836;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.v-theme--dark .modal-header {
  border-bottom-color: #4c566a;
}

.v-theme--dark .modal-header h3 {
  color: #eceff4;
}

.v-theme--dark .form-group label {
  color: #d8dee9;
}

.v-theme--dark .select-func {
  background: #3b4252;
  border-color: #4c566a;
  color: #eceff4;
}

.v-theme--dark .select-func option {
  background: #3b4252;
  color: #eceff4;
}

.v-theme--dark .form-hint,
.v-theme--dark .upload-hint {
  color: #81a1c1;
}

.v-theme--dark .tab-btn {
  background: #3b4252;
  border-color: #4c566a;
  color: #d8dee9;
}

.v-theme--dark .tab-btn:hover {
  border-color: #5e81ac;
  color: #88c0d0;
}

.v-theme--dark .tab-btn.active {
  border-color: #5e81ac;
  background: #434c6e;
  color: #88c0d0;
}

.v-theme--dark .canvas-area {
  background: #2e344c;
  border-color: #4c566a;
}

.v-theme--dark .signature-canvas {
  background: #3b4252;
  border-color: #4c566a;
}

.v-theme--dark .btn-clear {
  background: #3b4252;
  border-color: #4c566a;
  color: #d8dee9;
}

.v-theme--dark .btn-clear:hover {
  background: #4c566a;
  border-color: #d08770;
  color: #d08770;
}

.v-theme--dark .upload-area {
  background: #2e344c;
  border-color: #4c566a;
}

.v-theme--dark .file-input {
  color: #eceff4;
}

.v-theme--dark .modal-buttons {
  border-top-color: #4c566a;
}

.v-theme--dark .btn-secondary {
  background: #3b4252;
  color: #eceff4;
  border-color: #4c566a;
}

.v-theme--dark .btn-secondary:hover {
  background: #434c6e;
  border-color: #5e81ac;
}

.v-theme--dark .btn-primary {
  background: linear-gradient(135deg, #5e81ac 0%, #4c6a88 100%);
}

.v-theme--dark .btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #4c6a88 0%, #3d5570 100%);
}

.v-theme--dark .btn-add {
  background: linear-gradient(135deg, #5e81ac 0%, #4c6a88 100%);
}

.v-theme--dark .btn-add:hover {
  background: linear-gradient(135deg, #4c6a88 0%, #3d5570 100%);
}
</style>
